import React, { Component } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, OverlayTrigger, Popover, Row, Col, Button } from 'react-bootstrap';
import cn from 'classnames';
import styles from './ticket-spot.scss';
import { InputVerificationCode } from '../pin-verify';
import { WidgetLoader } from '../../loader/widget-loader';
import { errorHander } from '../../utils/error-handler';
import { StepButtonWrapper } from './checkout-stepper';

const darkModeCardStyle = {
  '.input-container': {
    borderRadius: '6px',
  },
  '.input-container.is-focus': {
    borderColor: '#006AFF',
  },
  '.input-container.is-error': {},
  '.message-text': {
    color: '#999999',
  },
  '.message-icon': {
    color: '#999999',
  },
  '.message-text.is-error': {
    color: '#ff1600',
  },
  '.message-icon.is-error': {
    color: '#ff1600',
  },
  input: {
    color: '#000',
    fontFamily: 'helvetica neue, sans-serif',
  },
  'input::placeholder': {
    color: '#999999',
  },
  'input.is-error': {
    color: '#ff1600',
  },
  '@media screen and (max-width: 600px)': {
    input: {
      fontSize: '12px',
    },
  },
};

export class SquarePaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      card: null,
      payments: null,
      savedCodeKey: uuidv4(),
      savedValue: false,
      processing: null,
      code: null,
      loading: true,
      openPinVerification: false,
    };
  }

  componentDidMount = async () => {
    const { locationId, checkoutValues, onResizeComponent } = this.props;
    const appId = process.env.SQUARE_APPLICATION_ID;
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    if (+checkoutValues.cartTotal === 0) {
      this.setState({
        loading: false,
      });
      return;
    }
    let payments;
    try {
      payments = await window.Square.payments(appId, locationId);
    } catch (e) {
      errorHander(e);

      console.log('Missing card details');
    }

    try {
      const card = await this.initializeCard(payments);
      this.setState({
        card,
        payments,
        loading: false,
      });
      onResizeComponent && onResizeComponent();
    } catch (e) {
      errorHander(e);
    }
  };

  initializeCard = async (payments) => {
    const card = await payments.card({
      style: darkModeCardStyle,
    });
    await card.attach('#card-container');
    return card;
  };

  verifyBuyer = async (payments, source, verificationDetails) => {
    const verificationResults = await payments.verifyBuyer(source, verificationDetails);
    return verificationResults.token;
  };

  handlePayment = async (ev) => {
    const { currency, checkoutValues, form, session } = this.props;
    const { first_name, last_name, email } = form;
    ev && ev.preventDefault();
    this.setState({ processing: true });
    try {
      const result = await this.state.card.tokenize();
      const { postalCode } = result.details.billing;
      if (result.status === 'OK') {
        const billingContact = {
          familyName: last_name,
          givenName: first_name,
          postalCode,
          email,
        };
        const verificationDetails = {
          amount: checkoutValues.cartTotal,
          billingContact,
          currencyCode: currency.toUpperCase(),
        };

        const verificationToken = await this.verifyBuyer(this.state.payments, result.token, { ...verificationDetails, intent: 'CHARGE' });

        const saveCardCallback = async () => {
          const saveCardResult = await this.state.card.tokenize();
          const verificationTokenStore = await this.verifyBuyer(this.state.payments, saveCardResult.token, { billingContact, intent: 'STORE' });

          return {
            verification_token: verificationTokenStore,
            idempotency_key: session.idempotency_key,
            payment_id: saveCardResult.token,
            first_name,
            last_name,
            email,
            postal_code: postalCode,
            save_code: this.state.code,
            saved_code_key: this.state.savedCodeKey,
          };
        };
        await this.props.onPaymentSubmit(
          {
            bank_token: result.token,
            payment_type: 'square',
            verification_token: verificationToken,
            idempotency_key: session.idempotency_key,
            postal_code: postalCode,
            should_save: this.state.savedValue,
          },
          this.state.savedValue ? saveCardCallback : null
        );
        this.setState({ processing: false });
      }
    } catch (e) {
      this.setState({ processing: false });
    }
  };
  handleSavedCardVerification = async (ev, session) => {
    const { checkoutValues, currency, selectedCard, form } = this.props;
    const { first_name, last_name, email } = form;
    ev && ev.preventDefault();
    try {
      const billingContact = {
        familyName: last_name,
        givenName: first_name,
        email,
      };
      const verificationDetails = {
        amount: checkoutValues.cartTotal,
        billingContact,
        currencyCode: currency.toUpperCase(),
      };

      const verificationToken = await this.verifyBuyer(this.state.payments, selectedCard.square_card_id, {
        ...verificationDetails,
        intent: 'CHARGE',
      });

      await this.props.onPaymentSubmit({
        saved_card_id: selectedCard.id,
        verification_token: verificationToken,
        idempotency_key: session.idempotency_key,
      });
      this.setState({ processing: false });
    } catch (e) {
      errorHander(e);

      this.setState({ processing: false });
    }
  };

  handleCheckboxChange = () => {
    this.setState({
      savedValue: !this.state.savedValue,
    });
  };

  togglePinVerification = () => {
    this.setState({
      openPinVerification: !this.state.openPinVerification,
    });
  };

  handleCodeSubmit = async (code) => {
    this.setState({
      code,
    });
    this.togglePinVerification();
    await this.handlePayment();
  };
  render() {
    const { session, selectedCard, onBack, settings, form, onPaymentSubmit, checkoutValues } = this.props;
    const { email } = form;

    if (+checkoutValues.cartTotal === 0) {
      return (
        <div className={styles.paymentButtonContainer}>
          {!this.state.savedValue &&
            React.cloneElement(this.props.children, {
              onClick: () => onPaymentSubmit({ idempotency_key: session.idempotency_key }),
            })}
        </div>
      );
    }

    const popover = (
      <Popover id="popover-basic" className={styles.popover}>
        <Popover.Content className={styles.detailsContent}>
          <InputVerificationCode saved_code_key={this.state.savedCodeKey} email={email} settings={settings} onSubmit={this.handleCodeSubmit} />
        </Popover.Content>
      </Popover>
    );

    if (session && session.cards && selectedCard) {
      return (
        <>
          <div className={styles.cardView}>
            <div className={cn('saved-card')}>
              {selectedCard.card_brand} ****-****-****-{selectedCard.last_4}
            </div>
            <div className={cn('saved-date')}>
              {selectedCard.exp_month}/{selectedCard.exp_year}
            </div>
          </div>
          <Row>
            <Col xs={12}>
              <Row className={styles.confirmQuestionsButton}>
                {React.cloneElement(this.props.children, {
                  onClick: (e) => this.handleSavedCardVerification(e, session),
                })}
              </Row>
            </Col>
          </Row>
          <div id="card-container" style={{ visibility: 'hidden' }} />
        </>
      );
    }
    return (
      <>
        <form id="payment-form">
          <div id="card-container" />
          <WidgetLoader loading={this.state.loading} isRelative>
            <>
              <div className={styles.confirmQuestionsButton}>
                {this.state.savedValue && (
                  <OverlayTrigger trigger="click" placement="top" overlay={popover} show={this.state.openPinVerification}>
                    {React.cloneElement(this.props.children, {
                      onClick: this.togglePinVerification,
                    })}
                  </OverlayTrigger>
                )}
                <div className={styles.paymentButtonContainer}>
                  <StepButtonWrapper>
                    <Button variant="outline-dark" onClick={onBack} className="checkout-prev-button checkout-back-button">
                      {settings.get('checkoutBackButtonText')}
                    </Button>
                  </StepButtonWrapper>
                  {!this.state.savedValue &&
                    React.cloneElement(this.props.children, {
                      disabled: this.state.processing,
                      onClick: this.handlePayment,
                    })}
                </div>
                {settings.get('enableFastCheckout') && (
                  <Form.Group controlId="formBasicCheckbox" className={styles.checkoutSaveCheckbox}>
                    <Form.Check
                      type="checkbox"
                      value={this.state.savedValue}
                      label={settings.get('fastCheckoutSaveDetails')}
                      onChange={this.handleCheckboxChange}
                    />
                  </Form.Group>
                )}
              </div>
            </>
          </WidgetLoader>
        </form>
        <div id="payment-status-container" />
      </>
    );
  }
}
